/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL THEME PROVIDER v2.0 — Bridge Pattern
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Envuelve el ThemeProvider v1 (ptel-theme-context) para mantener
 * compatibilidad con los ~30 consumidores v1, y añade tokens v2
 * (colors DS oficial) al contexto.
 *
 * Uso:
 *   import { ThemeProvider, useTheme } from '@/design-system/v2';
 *   // En root:   <ThemeProvider>…</ThemeProvider>
 *   // En comp:   const { isDark, toggleTheme, colors } = useTheme();
 *
 * Migración progresiva: los consumidores v1 siguen leyendo su contexto
 * original; los consumidores migrados a v2 obtienen colors DS oficial.
 *
 * @version 2.0.0
 * @date 2026-02-26
 * ══════════════════════════════════════════════════════════════════════════════
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import {
  ThemeProvider as V1ThemeProvider,
  useTheme as v1UseTheme,
} from '../ptel-theme-context';
import type { Theme, ThemeContextValue } from '../ptel-theme-context';
import { colors } from './design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS v2
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeV2ContextValue extends ThemeContextValue {
  /** Tokens de color DS v2 (design-tokens.ts) */
  colors: typeof colors;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT v2
// ─────────────────────────────────────────────────────────────────────────────

const ThemeV2Context = createContext<ThemeV2ContextValue | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// BRIDGE INTERNO — lee v1, publica v2
// ─────────────────────────────────────────────────────────────────────────────

function ThemeV2Bridge({ children }: { children: ReactNode }) {
  const v1 = v1UseTheme();

  const value = useMemo<ThemeV2ContextValue>(
    () => ({ ...v1, colors }),
    [v1],
  );

  return (
    <ThemeV2Context.Provider value={value}>
      {children}
    </ThemeV2Context.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER v2 (público)
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeProviderProps {
  children: ReactNode;
  defaultDark?: boolean;
}

export function ThemeProvider({ children, defaultDark = true }: ThemeProviderProps) {
  return (
    <V1ThemeProvider defaultDark={defaultDark}>
      <ThemeV2Bridge>{children}</ThemeV2Bridge>
    </V1ThemeProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK v2 (público)
// ─────────────────────────────────────────────────────────────────────────────

export function useTheme(): ThemeV2ContextValue {
  const ctx = useContext(ThemeV2Context);
  if (ctx === undefined) {
    throw new Error('useTheme v2 must be used within a <ThemeProvider> from @/design-system/v2');
  }
  return ctx;
}

// Re-export tipos v1 que los consumidores migrados pueden necesitar
export type { Theme, ThemeContextValue };
